/**
 * 登录界面组件
 */
import { createAPIClient } from '../api/client.js';
import type { APIClient } from '../api/client.js';

export interface LoginScreenOptions {
  appName: string;
  onLoginSuccess: (token: string, chatJid: string) => void;
}

export class LoginScreen {
  private options: LoginScreenOptions;
  private apiClient: APIClient;
  private form: HTMLFormElement | null = null;
  private usernameInput: HTMLInputElement | null = null;
  private passwordInput: HTMLInputElement | null = null;
  private errorDiv: HTMLElement | null = null;

  constructor(options: LoginScreenOptions) {
    this.options = options;
    this.apiClient = createAPIClient('');
  }

  /**
   * 初始化组件
   */
  private init(): void {
    this.form = document.getElementById('login-form') as HTMLFormElement;
    this.usernameInput = document.getElementById('username') as HTMLInputElement;
    this.passwordInput = document.getElementById('password') as HTMLInputElement;
    this.errorDiv = document.getElementById('login-error');

    if (!this.form) {
      console.error('Login form not found');
      return;
    }

    // 更新页面标题
    document.title = `${this.options.appName} - 登录`;

    // 绑定表单提交事件
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  /**
   * 处理表单提交
   */
  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    if (!this.usernameInput || !this.passwordInput) {
      return;
    }

    const username = this.usernameInput.value.trim();
    const password = this.passwordInput.value;

    // 清空错误信息
    this.showError('');

    // 禁用表单
    this.setFormDisabled(true);

    try {
      // 调用登录 API
      const response = await this.apiClient.login({ username, password });

      // 设置 Token
      this.apiClient.setToken(response.accessToken);

      // 触发成功回调
      this.options.onLoginSuccess(response.accessToken, response.chatJid);

      // 清空表单
      this.usernameInput.value = '';
      this.passwordInput.value = '';
    } catch (error) {
      console.error('Login error:', error);
      const message = error instanceof Error ? error.message : '登录失败，请重试';
      this.showError(message);
      this.setFormDisabled(false);
    }
  }

  /**
   * 显示错误信息
   */
  private showError(message: string): void {
    if (this.errorDiv) {
      this.errorDiv.textContent = message;
    }
  }

  /**
   * 设置表单禁用状态
   */
  private setFormDisabled(disabled: boolean): void {
    if (this.form) {
      const elements = this.form.elements;
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i] as HTMLInputElement | HTMLButtonElement;
        element.disabled = disabled;
      }
    }
  }

  /**
   * 显示登录界面
   */
  show(): void {
    this.init();

    const loginScreen = document.getElementById('login-screen');
    const chatScreen = document.getElementById('chat-screen');
    const settingsPanel = document.getElementById('settings-panel');

    if (loginScreen) loginScreen.style.display = 'flex';
    if (chatScreen) chatScreen.style.display = 'none';
    if (settingsPanel) settingsPanel.style.display = 'none';

    // 更新登录页面的应用名称
    const loginTitle = loginScreen?.querySelector('h1');
    if (loginTitle) {
      loginTitle.textContent = this.options.appName;
    }

    // 聚焦用户名输入框
    if (this.usernameInput) {
      this.usernameInput.focus();
    }
  }

  /**
   * 隐藏登录界面
   */
  hide(): void {
    const loginScreen = document.getElementById('login-screen');
    if (loginScreen) loginScreen.style.display = 'none';
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    if (this.form) {
      this.form.removeEventListener('submit', (e) => this.handleSubmit(e));
    }
  }
}
